import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { getSelectedOrganization } from "../../helpers/localStorage.ts";
import useSecureNavigate from "../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../hooks/useStores.tsx";
import { CampaignEntityStatus } from "../../models/common.ts";
import { TemplatePreviewBtn } from "../campaign-store.tsx";
import { PanelType } from "../constants/app-options.ts";
import { DesignEditorContext } from "../contexts/DesignEditor.tsx";
import useAppContext from "../hooks/useAppContext.tsx";
import { useEditor } from "../hooks/useEditor.tsx";
import { IDesign } from "../interfaces/DesignEditor.ts";
import { Template } from "../models/template.ts";
import { IScene } from "../types";
import useTemplateUtils from "./UseTemplateUtils.tsx";
interface SaveTemplateProps {
	templateName: string;
	setIsValidTemplateName?: (value: boolean) => void;
}

type CampaignHeaderUtils = {
	saveTemplate: (templateName?: string) => Promise<void>;
	saveNewTemplate: ({ templateName, setIsValidTemplateName }: SaveTemplateProps) => Promise<void>;
	buildUpdatedTemplate(updatedScenes?: IScene[], templateName?: string): Promise<Template | undefined>;
	getPreviewImageForFirstScene: (ads_json: IDesign) => Promise<string | undefined>;
};

const useCampaignHeaderUtils = (): CampaignHeaderUtils => {
	const navigate = useSecureNavigate();
	const { activePanel } = useAppContext();
	const { campaignStore } = useAppStore();
	const location = useLocation();
	const { exportTemplate } = useTemplateUtils();
	const editor = useEditor();
	const { isPopupView } = useContext(DesignEditorContext);
	const buildUpdatedTemplate = async (
		updatedScenes?: IScene[],
		templateName?: string,
	): Promise<Template | undefined> => {
		let selectedTemplate = !isPopupView ? campaignStore.selectedTemplate : campaignStore.selectedWizardTemplate;
		let updatedJson = await exportTemplate(updatedScenes);
		if (campaignStore.templateToDuplicate) {
			selectedTemplate = campaignStore.templateToDuplicate;
			if (!(
				campaignStore.selectedTemplate &&
				campaignStore?.selectedTemplate.id === campaignStore.templateToDuplicate.id
			)) {
				updatedJson = campaignStore.templateToDuplicate?.ads_json;
			}
		}
		if (selectedTemplate && updatedJson) {
			return {
				...selectedTemplate,
				ads_json: updatedJson,
				preview: (await getPreviewImageForFirstScene(updatedJson)) || "",
				status: CampaignEntityStatus.ACTIVE,
				name: templateName || selectedTemplate.name,
				org_id: getSelectedOrganization()?.organization?.uid,
				user_id: null,
			};
		}
		return undefined;
	};

	const saveTemplate = async (templateName?: string) => {
		const selectedTemplate = !isPopupView ? campaignStore.selectedTemplate : campaignStore.selectedWizardTemplate;
		if (selectedTemplate && !selectedTemplate?.id) {
			const newTemplate = await buildUpdatedTemplate(undefined, templateName);
			if (newTemplate) await campaignStore.createTemplate(newTemplate, navigate, location);
		} else {
			const updatedTemplate = await buildUpdatedTemplate(undefined, templateName);
			if (updatedTemplate) {
				await campaignStore.updateTemplate(updatedTemplate);
			}
		}
		if (activePanel === PanelType.TEMPLATES) {
			campaignStore.handleSetSelectedTemplatePreviewButton(TemplatePreviewBtn.ORGANIZATION);
			await campaignStore.loadTemplates(false);
		}
	};

	const saveNewTemplate = async ({ templateName, setIsValidTemplateName }: SaveTemplateProps) => {
		if (!templateName) {
			setIsValidTemplateName && setIsValidTemplateName(false);
			return;
		}

		const updatedTemplate = await buildUpdatedTemplate(undefined, templateName);
		if (updatedTemplate) await campaignStore.duplicateTemplate(updatedTemplate, navigate, location);
		campaignStore.setProperty("openDuplicateCampaignOrTemplateModal", false);
		campaignStore.setProperty("templateToDuplicate", null);
		if (activePanel === PanelType.TEMPLATES) {
			campaignStore.handleSetSelectedTemplatePreviewButton(TemplatePreviewBtn.ORGANIZATION);
			await campaignStore.loadTemplates(false);
		}

		campaignStore.setProperty("openDuplicateCampaignOrTemplateModal", false);
	};

	const getPreviewImageForFirstScene = async (ads_json: IDesign) => {
		const firstScene = ads_json.scenes[0];
		const firstLayer = firstScene.layers[0];
		const width = firstLayer.width;
		const height = firstLayer.height;
		firstScene.frame = {
			width: width,
			height: height,
		};
		const renderedImage: string | undefined = await editor?.renderer.render(firstScene);
		return renderedImage;
	};

	return {
		saveTemplate,
		saveNewTemplate,
		buildUpdatedTemplate,
		getPreviewImageForFirstScene,
	};
};

export default useCampaignHeaderUtils;
